import { useEffect } from 'react';
import { ApiService, AppService, LoggerFactory } from '@ibe/services';
import {
  ApiAirportType,
  ApiFlightSearchGeoUnitsRequest,
  ApiFlightSearchGeoUnitsRequestFromJSON,
  ApiGeoUnit,
  ApiItemType,
  ApiRoomContainer,
  ApiSearchGeoUnitsRequestFromJSON,
  ApiSearchGeoUnitsResponse
} from '@ibe/api';
import { useExternalSearchParams } from './ExternalSearchParamsContext';
import PackageSearchStore from './PackageSearchStore';
import useAppService from '../../Hooks/useAppService';
import useApi from '../../Hooks/useApi';
import useConfig from '../../Hooks/useConfig';
import isSearchDisabled from './externals';
import DestinationItem, { DestinationItemCategory } from '../../../Models/DestinationItem';

const logger = LoggerFactory.get('useSetInitialFromExternal.ts');

type GeoUnitPromise = Promise<{
  result: ApiSearchGeoUnitsResponse | undefined;
  propName: string;
}>;

const getGeoUnit = async (
  appService: AppService,
  api: ApiService,
  search: string,
  itemType: ApiItemType,
  propName: string,
  subType: string,
  typeCode: string,
  airportType?: ApiAirportType
): Promise<{ result: ApiSearchGeoUnitsResponse | undefined; propName: string }> => {
  const request = airportType
    ? ApiFlightSearchGeoUnitsRequestFromJSON({
        type: 'FlightSearchGeoUnitsRequest',
        search,
        offset: 0,
        limit: 100,
        itemType,
        airportType,
        languageCode: appService.lang
      } as ApiFlightSearchGeoUnitsRequest)
    : ApiSearchGeoUnitsRequestFromJSON({
        type: 'SearchGeoUnitsRequest',
        search,
        offset: 0,
        limit: 100,
        typeCodes: [typeCode],
        languageCode: appService.lang
      } as ApiFlightSearchGeoUnitsRequest);
  let result;
  try {
    result =
      appService.config.useIndexedGeoUnits && ApiItemType.TRANSPORT === subType
        ? await api.searchGeoUnitsElastic(request)
        : await api.searchGeoUnits(request);
  } catch (err) {
    logger.error(err);
  }
  return { result, propName };
};

const useSetInitialFromExternal = (
  submitCallBack: () => void,
  store: PackageSearchStore,
  setPaxSelection: (occupancy: ApiRoomContainer[]) => void,
  subType: ApiItemType,
  destinationMandatory?: boolean,
  endDateMandatory?: boolean,
  withCategorySelect?: boolean,
  hideOrigin?: boolean
) => {
  const context = useExternalSearchParams();
  const appService = useAppService();
  const api = useApi();
  const config = useConfig();

  useEffect(() => {
    if (context) {
      const {
        startDate,
        endDate,
        occupancy,
        origins,
        destination,
        triggerSearch,
        mainServiceCodes,
        duration
      } = context;
      if (startDate) {
        store.setStartDate(startDate);
      }
      if (endDate) {
        store.setEndDate(endDate);
      }
      if (duration) {
        store.setDuration(duration);
      }
      if (mainServiceCodes) {
        store.setParams({ mainServiceCodes });
      }

      store.setParams({ isFromSearchButton: false });
      if (mainServiceCodes?.length) {
        store.setParams({ goToDetails: true });
      }

      if (occupancy) {
        store.setOccupancy([occupancy]);
        setPaxSelection([occupancy]);
      }
      let geoUnitPromise: GeoUnitPromise[] = [];
      if (destination) {
        geoUnitPromise = [
          getGeoUnit(
            appService,
            api,
            destination.code || '',
            subType,
            'destination',
            subType,
            destination.typeCode || ''
          )
        ];
      }
      if (origins) {
        geoUnitPromise = [
          ...geoUnitPromise,
          getGeoUnit(
            appService,
            api,
            '',
            ApiItemType.TRANSPORT,
            'origins',
            '',
            '',
            ApiAirportType.ORIGIN
          )
        ];
      }

      if (geoUnitPromise.length > 0) {
        Promise.all(geoUnitPromise).then(result => {
          result.forEach(res => {
            if (res.propName === 'destination') {
              const geoUnits = res.result?.geoUnits;
              if (geoUnits) {
                const foundDestination = geoUnits.find(
                  unit => unit.code === destination?.code && unit.typeCode === destination?.typeCode
                );
                if (foundDestination) {
                  if (config.useIndexedGeoUnits) {
                    const { code, id, typeCode, name } = foundDestination;
                    const destinationItem: DestinationItem = {
                      category: DestinationItemCategory.GEO_UNIT,
                      code,
                      id,
                      typeCode,
                      name
                    };
                    store.setDestinationItem(destinationItem);
                  } else {
                    store.setDestination(foundDestination);
                  }
                }
              }
            } else if (res.propName === 'origins') {
              const geoUnits = res.result?.geoUnits;
              if (geoUnits) {
                const foundOrigins = getAllGeoUnits(geoUnits).filter(unit =>
                  origins?.some(origin => origin === unit.code || origin === unit.id)
                );
                if (foundOrigins.length > 0) {
                  store.setOrigins(foundOrigins);
                }
              }
            }
            if (
              !isSearchDisabled({
                store,
                destinationMandatory,
                endDateMandatory,
                withCategorySelect,
                hideOrigin,
                originMustBeSet: false,
                mainServiceCodes
              }) &&
              triggerSearch
            ) {
              submitCallBack();
            }
          });
        });
      } else if (
        !isSearchDisabled({
          store,
          destinationMandatory,
          endDateMandatory,
          withCategorySelect,
          hideOrigin,
          originMustBeSet: false,
          mainServiceCodes
        }) &&
        triggerSearch
      ) {
        submitCallBack();
      }
    }
  }, [context]);
};

export default useSetInitialFromExternal;

const getAllGeoUnits = (geoUnits: ApiGeoUnit[] | undefined): ApiGeoUnit[] => {
  const allGeoUnits: ApiGeoUnit[] = [];
  (geoUnits || []).map(geoUnit => {
    if (geoUnit.typeCode !== 'PSEUDO') {
      allGeoUnits.push(geoUnit);
    } else {
      Object.keys(geoUnit.children).map(key => {
        const geoChild = geoUnit.children[key];
        allGeoUnits.push(geoChild);
      });
    }
  });
  return allGeoUnits;
};
